import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Box } from 'foundations-components/transitional/components/Box';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { Overhang } from 'foundations-components/transitional/components/Overhang';
import classNames from 'classnames';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { DeviceFrame } from 'foundations-components/transitional/components/DeviceFrame';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { useFocusCloseButton } from 'marketplace-ui-common/hooks/useFocusCloseButton';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AssetShareButton from './AssetShareButton';
import FullscreenModalHeader from './FullscreenModalHeader';
import FullscreenModalHeaderGroup from './FullscreenModalHeaderGroup';
import FullscreenModalHeading from './FullscreenModalHeading';
import { DesktopIcon } from 'foundations-assets/icons/components/DesktopIcon';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import { MobileIcon } from 'foundations-assets/icons/components/MobileIcon';
import { TabletIcon } from 'foundations-assets/icons/components/TabletIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SUBSCREEN = 'preview modal';
const deviceIcons = {
  desktop: /*#__PURE__*/_jsx(DesktopIcon, {
    size: "200",
    alignWithText: true
  }),
  tablet: /*#__PURE__*/_jsx(TabletIcon, {
    size: "200",
    alignWithText: true
  }),
  mobile: /*#__PURE__*/_jsx(MobileIcon, {
    size: "100",
    alignWithText: true
  })
};
export default function AssetDemoModal({
  exampleUrl,
  name,
  onClose,
  onTrackInteraction,
  onTrackPageview,
  providerName,
  primaryButton,
  showShareButton = true,
  slug
}) {
  const [activeDevice, setActiveDevice] = useState('desktop');
  const [focused, setFocused] = useState(false);
  const demoModalBackButtonRef = useRef(null);
  const {
    isMobileView,
    isMobileViewHeader
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.LG]: false
    },
    isMobileViewHeader: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const primaryButtonClassName = classNames('m-top-4', primaryButton.props.className);
  const deviceFrameWidth = useMemo(() => {
    switch (activeDevice) {
      case 'tablet':
        return '768px';
      case 'mobile':
        return '375px';
      default:
        return '100%';
    }
  }, [activeDevice]);
  const handleTrackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      subscreen: SUBSCREEN
    }));
  }, [onTrackInteraction]);
  const renderDeviceButton = useCallback(device => {
    return /*#__PURE__*/_jsxs(Button, {
      active: activeDevice === device,
      onClick: () => {
        setActiveDevice(device);
        handleTrackInteraction('set preview device', {
          value: device
        });
      },
      size: "small",
      use: "tertiary-light",
      children: [deviceIcons[device], /*#__PURE__*/_jsx(FormattedMessage, {
        message: `marketplace-assets-ui.details.demoModal.${device}`
      })]
    });
  }, [activeDevice, handleTrackInteraction]);
  useEffect(() => {
    onTrackPageview({
      subscreen: SUBSCREEN
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isMobileView) {
      setActiveDevice('desktop');
    }
  }, [isMobileView]);
  useEffect(() => {
    if (demoModalBackButtonRef) {
      setFocused(demoModalBackButtonRef.current != null);
    }
  }, []);
  useFocusCloseButton(focused, demoModalBackButtonRef.current);
  return /*#__PURE__*/_jsxs(Modal, {
    use: "fullscreen",
    children: [/*#__PURE__*/_jsxs(FullscreenModalHeader, {
      isMobileView: isMobileViewHeader,
      children: [/*#__PURE__*/_jsx(FullscreenModalHeaderGroup, {
        children: /*#__PURE__*/_jsxs(Button, {
          "aria-label": unescapedText('marketplace-assets-ui.details.screenshots.modal.backButton'),
          buttonRef: demoModalBackButtonRef,
          onClick: () => {
            onClose();
            handleTrackInteraction('close preview modal');
          },
          use: "link",
          children: [/*#__PURE__*/_jsx(Flex, {
            paddingBlockStart: isMobileViewHeader ? '200' : undefined,
            marginInlineEnd: isMobileViewHeader ? 'auto' : undefined,
            children: /*#__PURE__*/_jsx(LeftIcon, {
              size: isMobileViewHeader ? '500' : '200'
            })
          }), /*#__PURE__*/_jsx(MediaQueryView, {
            min: "md",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.details.screenshots.modal.backButton"
            })
          })]
        })
      }), /*#__PURE__*/_jsxs(FullscreenModalHeaderGroup, {
        isMobileHeader: isMobileViewHeader,
        position: "center",
        children: [/*#__PURE__*/_jsx(FullscreenModalHeading, {
          subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.details.demoModal.providerName",
            options: {
              providerName
            }
          }),
          title: name
        }), /*#__PURE__*/_jsx(MediaQueryView, {
          max: "md",
          children: /*#__PURE__*/cloneElement(primaryButton, {
            className: primaryButtonClassName
          })
        })]
      }), /*#__PURE__*/_jsx(FullscreenModalHeaderGroup, {
        position: "right",
        children: /*#__PURE__*/_jsxs(ButtonWrapper, {
          justify: "end",
          children: [showShareButton && /*#__PURE__*/_jsx(AssetShareButton, {
            "aria-label": unescapedText('marketplace-assets-ui.details.screenshots.modal.shareButton'),
            responsive: true,
            slug: slug,
            use: "transparent"
          }), /*#__PURE__*/_jsx(MediaQueryView, {
            min: "md",
            children: primaryButton
          })]
        })
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      style: {
        position: 'relative'
      },
      paddingBlockStart: "0",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        blockSize: "100%",
        children: [/*#__PURE__*/_jsx(MediaQueryView, {
          inlineSize: "100%",
          min: "lg",
          children: /*#__PURE__*/_jsxs(Grid, {
            children: [/*#__PURE__*/_jsx(GridItem, {
              size: 3
            }), /*#__PURE__*/_jsx(GridItem, {
              textAlign: "center",
              size: 6,
              children: /*#__PURE__*/_jsx(Box, {
                basis: 0,
                grow: 1,
                paddingBlock: "500",
                children: /*#__PURE__*/_jsxs(ButtonGroup, {
                  children: [renderDeviceButton('desktop'), renderDeviceButton('tablet'), renderDeviceButton('mobile')]
                })
              })
            }), /*#__PURE__*/_jsx(GridItem, {
              textAlign: "right",
              size: 3,
              alignSelf: "center",
              children: /*#__PURE__*/_jsx(Button, {
                external: true,
                href: exampleUrl,
                onClick: () => handleTrackInteraction('click preview link'),
                use: "link",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "marketplace-assets-ui.details.demoModal.externalLink"
                })
              })
            })]
          })
        }), /*#__PURE__*/_jsx(View, {
          paddingBlockStart: isMobileView ? '600' : undefined,
          alignSelf: activeDevice === 'desktop' ? 'stretch' : 'center',
          flexGrow: 1,
          flexShrink: 1,
          overflow: "auto",
          flexBasis: "100%",
          inlineSize: deviceFrameWidth,
          children: /*#__PURE__*/_jsx(DeviceFrame, {
            type: activeDevice,
            blockSize: activeDevice === 'desktop' ? '100%' : undefined,
            children: /*#__PURE__*/_jsx("iframe", {
              "aria-label": unescapedText('marketplace-assets-ui.details.demoModal.iframeAriaLabel', {
                name
              }),
              frameBorder: 0,
              height: "100%",
              scrolling: "yes",
              src: exampleUrl,
              style: {
                display: 'block'
              },
              title: unescapedText('marketplace-assets-ui.details.demoModal.iframeTitle', {
                name
              }),
              width: "100%"
            })
          })
        })]
      }), /*#__PURE__*/_jsx(Overhang, {
        side: "top",
        gradientColor: "do-not-use-obsidian",
        opacity: 0.1,
        size: 4
      })]
    })]
  });
}